import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Building2, Phone, Mail, MapPin, CreditCard, FileText, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Supplier, supplierApiService } from "../services/supplierApi";

interface SupplierData {
  supplierName: string;
  faxNo: string;
  email: string;
  contactNo: string;
  landLineNo: string;
  panNo: string;
  gstNo: string;
  stateName: string;
  stateCode: string;
  bankName: string;
  ifscCode: string;
  bankAccountNo: string;
  billAddress: string;
  shipAddress: string;
  location: string;
  country: string;
  countryCurrency: string;
  isActive: boolean;
}

export function SupplierInformationForm() {
  const location = useLocation() as { state?: { mode?: 'new'|'edit'|'view'; supplier?: Supplier } };
  const navigate = useNavigate();
  const routeMode = location.state?.mode;
  const routeSupplier = location.state?.supplier;

  const resolvedSupplier = routeSupplier ?? null;
  const resolvedViewMode = routeMode === 'view' ? true : (routeMode === 'edit' ? false : false);

  const [formData, setFormData] = useState<SupplierData>({
    supplierName: '',
    faxNo: '',
    email: '',
    contactNo: '',
    landLineNo: '',
    panNo: '',
    gstNo: '',
    stateName: '',
    stateCode: '',
    bankName: '',
    ifscCode: '',
    bankAccountNo: '',
    billAddress: '',
    shipAddress: '',
    location: '',
    country: '',
    countryCurrency: '',
    isActive: true
  });

  // Populate form data when supplier prop changes
  useEffect(() => {
    const source = resolvedSupplier;
    if (source) {
      setFormData({
        supplierName: source.supplierName || '',
        faxNo: source.faxNo || '',
        email: source.email || '',
        contactNo: source.contactNo || '',
        landLineNo: source.landLineNo || '',
        panNo: source.panNo || '',
        gstNo: source.gstNo || '',
        stateName: source.stateName || '',
        stateCode: source.stateCode || '',
        bankName: source.bankName || '',
        ifscCode: source.ifscCode || '',
        bankAccountNo: source.bankAccountNo || '',
        billAddress: source.billAddress || '',
        shipAddress: source.shipAddress || '',
        location: source.location || '',
        country: source.country || '',
        countryCurrency: source.countryCurrency || '',
        isActive: source.isActive !== undefined ? source.isActive : true
      });
    } else {
      // Reset form when no supplier (new supplier mode)
      setFormData({
        supplierName: '',
        faxNo: '',
        email: '',
        contactNo: '',
        landLineNo: '',
        panNo: '',
        gstNo: '',
        stateName: '',
        stateCode: '',
        bankName: '',
        ifscCode: '',
        bankAccountNo: '',
        billAddress: '',
        shipAddress: '',
        location: '',
        country: '',
        countryCurrency: '',
        isActive: true
      });
    }
  }, [resolvedSupplier]);

  // Static data (in real app, this would come from the database)
  const [states] = useState<string[]>([
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
    'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu and Kashmir', 'Ladakh'
  ]);

  const handleInputChange = (field: keyof SupplierData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    
    try {
      // Prepare supplier data for API
      const supplierData = {
        supplierName: formData.supplierName,
        faxNo: formData.faxNo,
        email: formData.email,
        contactNo: formData.contactNo,
        landLineNo: formData.landLineNo,
        panNo: formData.panNo,
        gstNo: formData.gstNo,
        stateName: formData.stateName,
        stateCode: formData.stateCode,
        bankName: formData.bankName,
        ifscCode: formData.ifscCode,
        bankAccountNo: formData.bankAccountNo,
        billAddress: formData.billAddress,
        shipAddress: formData.shipAddress,
        location: formData.location,
        country: formData.country,
        countryCurrency: formData.countryCurrency,
        isActive: formData.isActive
      };

      if (routeMode === 'edit' && resolvedSupplier) {
        // Update existing supplier
        await supplierApiService.updateSupplier(resolvedSupplier.id.toString(), supplierData);
        toast.success('Supplier updated successfully!');
      } else {
        // Create new supplier
        await supplierApiService.createSupplier(supplierData);
        toast.success('Supplier created successfully!');
      }
      
      navigate('/suppliers');
    } catch (error) {
      // console.error('Failed to save supplier:', error);
      toast.error(routeMode === 'edit' ? 'Failed to update supplier' : 'Failed to create supplier');
    }
  };

  const handleCancel = () => {
    navigate('/suppliers');
  };

  const copyBillingToShipping = () => {
    setFormData(prev => ({
      ...prev,
      shipAddress: prev.billAddress
    }));
    toast.info('Billing address copied to shipping address');
  };

  return (
    <Card className="w-full border-0 shadow-lg bg-white/80 backdrop-blur-sm" style={{ position: 'relative', zIndex: 1 }}>
      <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <CardTitle className="text-xl text-gray-900">
              {resolvedViewMode ? 'View Supplier' : resolvedSupplier ? 'Edit Supplier' : 'Add New Supplier'}
            </CardTitle>
            <CardDescription className="text-primary-100">
              {resolvedViewMode ? 'View supplier details' : resolvedSupplier ? 'Update supplier information' : 'Enter supplier details, contact information, and banking details'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSave} className="space-y-8">
          {/* Basic Information Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Basic Information</h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="supplierName" className="text-gray-700">Supplier Name</Label>
                <Input
                  id="supplierName"
                  value={formData.supplierName}
                  onChange={(e) => handleInputChange('supplierName', e.target.value)}
                  placeholder="Enter supplier name"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={resolvedViewMode}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="state" className="text-gray-700">State</Label>
                <Select 
                  value={formData.stateName} 
                  onValueChange={(value) => handleInputChange('stateName', value)}
                  disabled={resolvedViewMode}
                >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search states...">
                    {states.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Contact Information</h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter email address"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={resolvedViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile" className="text-gray-700">Mobile Number</Label>
                <Input
                  id="mobile"
                  value={formData.contactNo}
                  onChange={(e) => handleInputChange('contactNo', e.target.value)}
                  placeholder="Enter mobile number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={resolvedViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="landline" className="text-gray-700">Landline Number</Label>
                <Input
                  id="landline"
                  value={formData.landLineNo}
                  onChange={(e) => handleInputChange('landLineNo', e.target.value)}
                  placeholder="Enter landline number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={resolvedViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fax" className="text-gray-700">Fax</Label>
                <Input
                  id="fax"
                  value={formData.faxNo}
                  onChange={(e) => handleInputChange('faxNo', e.target.value)}
                  placeholder="Enter fax number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={resolvedViewMode}
                />
              </div>
            </div>
          </div>

          {/* Regulatory Information Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Regulatory Information</h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="panNumber" className="text-gray-700">PAN Number</Label>
                <Input
                  id="panNumber"
                  value={formData.panNo}
                  onChange={(e) => handleInputChange('panNo', e.target.value)}
                  placeholder="Enter PAN number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={resolvedViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gstNumber" className="text-gray-700">GST Number</Label>
                <Input
                  id="gstNumber"
                  value={formData.gstNo}
                  onChange={(e) => handleInputChange('gstNo', e.target.value)}
                  placeholder="Enter GST number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={resolvedViewMode}
                />
              </div>
            </div>
          </div>

          {/* Banking Details Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Banking Details</h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="bankName" className="text-gray-700">Bank Name</Label>
                <Input
                  id="bankName"
                  value={formData.bankName}
                  onChange={(e) => handleInputChange('bankName', e.target.value)}
                  placeholder="Enter bank name"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={resolvedViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ifscCode" className="text-gray-700">IFSC Code</Label>
                <Input
                  id="ifscCode"
                  value={formData.ifscCode}
                  onChange={(e) => handleInputChange('ifscCode', e.target.value)}
                  placeholder="Enter IFSC code"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={resolvedViewMode}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="accountNumber" className="text-gray-700">Account Number</Label>
                <Input
                  id="accountNumber"
                  value={formData.bankAccountNo}
                  onChange={(e) => handleInputChange('bankAccountNo', e.target.value)}
                  placeholder="Enter account number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={resolvedViewMode}
                />
              </div>
            </div>
          </div>

          {/* Address Information Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Address Information</h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="billingAddress" className="text-gray-700">Billing Address *</Label>
                </div>
                <Textarea
                  id="billingAddress"
                  value={formData.billAddress}
                  onChange={(e) => handleInputChange('billAddress', e.target.value)}
                  placeholder="Enter billing address"
                  rows={4}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={resolvedViewMode}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="shippingAddress" className="text-gray-700">Shipping Address</Label>
                  {!resolvedViewMode && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={copyBillingToShipping}
                      className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600 text-xs h-8"
                    >
                      Copy from billing
                    </Button>
                  )}
                </div>
                <Textarea
                  id="shippingAddress"
                  value={formData.shipAddress}
                  onChange={(e) => handleInputChange('shipAddress', e.target.value)}
                  placeholder="Enter shipping address or copy from billing address"
                  rows={4}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={resolvedViewMode}
                />
              </div>
            </div>
          </div>

          {/* Active/Inactive Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) => handleInputChange('isActive', checked as boolean)}
              disabled={resolvedViewMode}
            />
            <Label htmlFor="isActive" className="text-sm text-primary-600">
              Is Active
            </Label>
          </div>

          {/* Action Buttons */}
          {!resolvedViewMode && (
            <div className="flex items-center justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-primary-600 hover:bg-primary-700"
              >
                Save Supplier
              </Button>
            </div>
          )}

          {resolvedViewMode && (
            <div className="flex items-center justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Close
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}